import { jwtDecode } from "jwt-decode";

/** Map Membership.role (ADR-0003) → legacy Django group names for route gates. */
const ROLE_TO_GROUPS = {
  PLATFORM_ADMIN: ["Company Admin"],
  TENANT_ADMIN: ["Company Admin"],
  CASHIER: ["Company Admin"],
  DRIVER: ["Company Driver"],
  CARRIER_MANAGER: ["Company Admin"],
  AGENT_MANAGER: ["Agent Manager"],
};

export const getDecodedToken = () => {
  const token = localStorage.getItem("access_token");
  if (!token) return null;

  try {
    return jwtDecode(token);
  } catch (error) {
    return null;
  }
};

export const getUserEmail = () => {
  const decoded = getDecodedToken();
  return decoded?.email || null;
};

/** Prefer JWT role; fall back to groups claim (TD-05 / #134). */
export const getUserRole = () => {
  const decoded = getDecodedToken();
  return decoded?.role || null;
};

export const getOrganizationSlug = () => {
  const decoded = getDecodedToken();
  return decoded?.organization_slug || null;
};

export const getOrganizationId = () => {
  const decoded = getDecodedToken();
  return decoded?.organization_id ?? null;
};

export const getUserGroups = () => {
  const decoded = getDecodedToken();
  const fromClaim = decoded?.groups;
  if (Array.isArray(fromClaim) && fromClaim.length > 0) {
    return fromClaim;
  }
  const role = decoded?.role;
  if (role && ROLE_TO_GROUPS[role]) {
    return ROLE_TO_GROUPS[role];
  }
  return [];
};

export const userHasGroup = (group) => {
  const groups = getUserGroups();
  return groups.includes(group);
};

export const userHasAnyGroup = (groupList) => {
  const groups = getUserGroups();
  return groupList.some((g) => groups.includes(g));
};

/** True if JWT role matches or maps to an allowed Django group name. */
export const userHasAnyRoleOrGroup = (allowedGroups = []) => {
  if (!allowedGroups.length) return true;
  const role = getUserRole();
  if (role) {
    const mapped = ROLE_TO_GROUPS[role] || [];
    if (allowedGroups.some((g) => mapped.includes(g))) return true;
    // Direct role name match if callers pass PLATFORM_ADMIN etc.
    if (allowedGroups.includes(role)) return true;
  }
  return userHasAnyGroup(allowedGroups);
};

export const isAuthenticated = () => {
  return !!localStorage.getItem("access_token");
};

/** Host-tenant admin/user — JWT is_host_tenant or organization/company slug. */
export const isDefaultCompanyUser = () => {
  const decoded = getDecodedToken();
  if (!decoded) return false;
  if (typeof decoded.is_host_tenant === "boolean") {
    return decoded.is_host_tenant;
  }
  const slug = decoded.organization_slug;
  return slug === "sv-city-trans";
};

/** Platform (host) admin — onboards tenants, LiqPay, route assignment. */
export const isPlatformAdminUser = () => {
  const role = getUserRole();
  if (role === "PLATFORM_ADMIN") return true;
  if (role) return false;
  return isDefaultCompanyUser() && userHasGroup("Company Admin");
};

/** Tenant admin — settings and ops for their own organization only. */
export const isTenantAdminUser = () => {
  const role = getUserRole();
  if (role === "TENANT_ADMIN") return true;
  if (role) return false;
  return userHasGroup("Company Admin") && !isDefaultCompanyUser();
};
